import React, { useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Loader2, ShieldCheck, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// OAuth 2.1 consent screen for the Jarvis PM MCP (Phase 3).
//
// The backend's /api/mcp/oauth/authorize validates the request and redirects the
// browser here with the OAuth params in the query. This page ensures the user is
// logged into Jarvis, shows what the connecting app will be able to do, and on
// Allow/Deny POSTs to /api/mcp/oauth/authorize/decision (cookie-authenticated),
// then navigates the browser to the returned client callback URL.

// Human-readable descriptions for the scopes we grant.
const SCOPE_LABELS: Record<string, string> = {
  'mcp:read': 'Read your projects, tasks, and comments',
  'mcp:write': 'Create and update tasks, post comments, and manage board sections',
};

const OAuthConsent: React.FC = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [working, setWorking] = useState(false);
  const [denied, setDenied] = useState(false);

  // Pull the OAuth params the backend forwarded.
  const params = {
    client_id: searchParams.get('client_id') || '',
    client_name: searchParams.get('client_name') || 'An application',
    redirect_uri: searchParams.get('redirect_uri') || '',
    code_challenge: searchParams.get('code_challenge') || '',
    code_challenge_method: searchParams.get('code_challenge_method') || 'S256',
    state: searchParams.get('state') || '',
    scope: searchParams.get('scope') || 'mcp:read mcp:write',
    resource: searchParams.get('resource') || '',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) {
    // Remember where to return, then send to the normal Jarvis login. Set this
    // synchronously (not in an effect) — the <Navigate> below unmounts us before
    // an effect would run, so an effect-based save would be lost.
    sessionStorage.setItem('post_login_redirect', window.location.pathname + window.location.search);
    return <Navigate to="/login" replace />;
  }

  // A malformed link (missing the essentials) can't be consented.
  if (!params.client_id || !params.redirect_uri || !params.code_challenge) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Invalid authorization request</h1>
          <p className="text-sm text-gray-500 mt-2">
            This connection link is missing required information. Please start the connection again from your app.
          </p>
        </div>
      </div>
    );
  }

  const decide = async (approve: boolean) => {
    // On Deny, don't bounce the user to the client's callback with a raw
    // error — show a clean "Access denied" screen right here instead.
    if (!approve) {
      setDenied(true);
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post('/api/mcp/oauth/authorize/decision', { ...params, approve: true });
      // Navigate the browser to the client's callback (an external URL).
      window.location.href = data.redirect;
    } catch (err: any) {
      toast.error(err?.response?.data?.error_description || err?.response?.data?.error || 'Could not complete the request.');
      setWorking(false);
    }
  };

  // Clean denied screen (no redirect to the client, no raw error code).
  if (denied) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6 bg-gray-50">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
            <X className="w-6 h-6 text-gray-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mt-4">Access denied</h1>
          <p className="text-sm text-gray-500 mt-2">
            You didn't connect {params.client_name} to Jarvis PM. Nothing was shared — you can
            close this tab.
          </p>
        </div>
      </div>
    );
  }

  const scopes = params.scope.split(/\s+/).filter(Boolean);

  return (
    <div className="flex items-center justify-center min-h-screen px-6 bg-gray-50">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mt-4">
            Connect {params.client_name} to Jarvis PM
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Signed in as <span className="font-medium text-gray-700">{user.email}</span>
          </p>
        </div>

        <div className="mt-6 border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {params.client_name} will be able to:
          </p>
          <ul className="space-y-2">
            {scopes.map((s) => (
              <li key={s} className="flex items-start gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>{SCOPE_LABELS[s] || s}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3">
            It acts as you, limited to what your Jarvis account can access. You can revoke this
            anytime. Comments can only be created, never silently edited or deleted.
          </p>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => decide(false)}
            disabled={working}
            className="flex-1 flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" /> Deny
          </button>
          <button
            onClick={() => decide(true)}
            disabled={working}
            className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {working ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
              </>
            ) : (
              <>
                <Check className="w-4 h-4" /> Allow
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-center text-gray-400 mt-3">
          {working
            ? `Connecting… taking you back to ${params.client_name}.`
            : `After you allow, you'll return to ${params.client_name} — it will be connected and ready to use.`}
        </p>
      </div>
    </div>
  );
};

export default OAuthConsent;
